import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { Score, ScoreTimeline } from '@/types';
import { getScores, getTimeline, deleteScore as apiDeleteScore } from '@/services/api';

interface ScoreState {
  // Data
  scores: Score[];
  currentScoreId: string | null;
  isLoading: boolean;
  error: string | null;

  // Computed
  currentScore: Score | null;
  currentTimeline: ScoreTimeline | null;

  // Actions
  addScore: (score: Score) => void;
  updateScore: (id: string, updates: Partial<Score>) => void;
  deleteScore: (id: string) => Promise<void>;
  setCurrentScore: (id: string | null) => void;
  setTimeline: (scoreId: string, timeline: ScoreTimeline) => void;
  fetchTimeline: (scoreId: string) => Promise<void>;
  fetchScores: () => Promise<void>;
}

export const useScoreStore = create<ScoreState>()(
  persist(
    immer((set, get) => ({
      scores: [],
      currentScoreId: null,
      isLoading: false,
      error: null,

      get currentScore() {
        const state = get();
        return state.scores.find((s) => s.id === state.currentScoreId) || null;
      },

      get currentTimeline() {
        const currentScore = get().currentScore;
        return currentScore?.timeline || null;
      },

      addScore: (score) =>
        set((state) => {
          state.scores.push(score);
        }),

      updateScore: (id, updates) =>
        set((state) => {
          const index = state.scores.findIndex((s) => s.id === id);
          if (index !== -1) {
            state.scores[index] = { ...state.scores[index], ...updates };
          }
        }),

      deleteScore: async (id) => {
        try {
          await apiDeleteScore(id);
        } catch (error) {
          console.error(
            '[scoreStore/deleteScore] Failed: API delete | reason=',
            error,
            '| score_id=',
            id
          );
          throw error;
        }
        set((state) => {
          state.scores = state.scores.filter((s) => s.id !== id);
          if (state.currentScoreId === id) {
            state.currentScoreId = null;
          }
        });
      },

      setCurrentScore: (id) =>
        set((state) => {
          state.currentScoreId = id;
        }),

      setTimeline: (scoreId, timeline) =>
        set((state) => {
          const index = state.scores.findIndex((s) => s.id === scoreId);
          if (index !== -1) {
            state.scores[index].timeline = timeline;
          }
        }),

      fetchTimeline: async (scoreId) => {
        try {
          const timeline = await getTimeline(scoreId);
          set((state) => {
            const index = state.scores.findIndex((s) => s.id === scoreId);
            if (index !== -1) {
              state.scores[index].timeline = timeline;
            }
          });
        } catch (error) {
          console.error(
            '[scoreStore/fetchTimeline] Failed: | reason=',
            error,
            '| score_id=',
            scoreId
          );
        }
      },

      fetchScores: async () => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        try {
          const scores = await getScores();
          set((state) => {
            state.scores = scores;
            state.isLoading = false;
          });
        } catch (error) {
          console.error('[scoreStore/fetchScores] Failed: fetch scores | reason=', error);
          set((state) => {
            state.error = error instanceof Error ? error.message : 'Failed to fetch scores';
            state.isLoading = false;
          });
        }
      },
    })),
    {
      name: 'otoflow-scores',
      partialize: (state) => ({
        currentScoreId: state.currentScoreId,
      }),
    }
  )
);
